import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ProductGallery from '../components/ProductGallery';
import { PageSeo } from '../components/Seo';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import { canAddToCart, getPrimaryImage } from '../utils/product';
import { formatPrice } from '../utils/formatPrice';
import { breadcrumbJsonLd, productJsonLd } from '../utils/structuredData';

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProductById, loadProduct, isLoading } = useProducts();
  const { addToCart, items } = useCart();
  const [quantity, setQuantity] = useState(1);
  const product = id ? getProductById(id) : undefined;
  const [ready, setReady] = useState(Boolean(product));

  useEffect(() => {
    if (!id) {
      setReady(true);
      return;
    }

    if (getProductById(id)) {
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);

    void loadProduct(id).finally(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [id, getProductById, loadProduct]);

  const inCart = product ? (items.find((i) => i.product.id === product.id)?.quantity ?? 0) : 0;
  const maxQuantity = product ? Math.max(0, product.stock - inCart) : 0;

  if (!ready || (isLoading && !product)) {
    return (
      <div className="container empty-state">
        <p>Ürün yükleniyor...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container empty-state">
        <PageSeo title="Ürün Bulunamadı" description="Aradığınız ürün bulunamadı." noindex />
        <h1>Ürün bulunamadı</h1>
        <Link to="/shop" className="btn btn-primary">Ürünlere Dön</Link>
      </div>
    );
  }

  const addable = canAddToCart(product);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    navigate('/cart');
  };

  const productImage = getPrimaryImage(product);

  return (
    <div className="container product-detail">
      <PageSeo
        title={product.name}
        description={product.description}
        path={`/product/${product.id}`}
        image={productImage}
        type="product"
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Ana Sayfa', path: '/' },
            { name: 'Ürünler', path: '/shop' },
            { name: product.name, path: `/product/${product.id}` },
          ]),
          productJsonLd(product),
        ]}
      />
      <Link to="/shop" className="back-link">
        <ArrowLeft size={16} aria-hidden />
        Ürünlere Dön
      </Link>

      <div className="product-detail-grid">
        <div className="product-detail-image">
          <ProductGallery images={product.images} alt={product.name} />
          {!product.inStock && <span className="out-of-stock-badge">Stokta Yok</span>}
        </div>

        <div className="product-detail-info">
          <span className="product-category">{product.category}</span>
          <h1>{product.name}</h1>
          <p className="product-detail-price">{formatPrice(product)}</p>
          <p className={`stock-info ${product.stock <= 5 && product.inStock ? 'stock-low' : ''}`}>
            {product.inStock ? `Stok: ${product.stock} adet` : 'Stokta yok'}
          </p>
          <p className="product-detail-desc">{product.description}</p>

          <div className="benefits">
            <h3>Öne Çıkan Özellikler</h3>
            <ul>
              {product.benefits.map((benefit) => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>
          </div>

          {product.specs && (
            <div className="product-specs">
              <h3>Ürün Özellikleri</h3>
              <table>
                <tbody>
                  {Object.entries(product.specs).map(([key, value]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {addable && maxQuantity > 0 && (
            <div className="quantity-selector">
              <label htmlFor="quantity">Adet:</label>
              <div className="quantity-controls">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  aria-label="Azalt"
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span id="quantity">{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                  aria-label="Artır"
                  disabled={quantity >= maxQuantity}
                >
                  +
                </button>
              </div>
              {inCart > 0 && (
                <span className="form-hint">Sepette {inCart} adet var (max {maxQuantity})</span>
              )}
            </div>
          )}

          {!product.inStock ? (
            <button className="btn btn-primary btn-lg" disabled>
              Stokta Yok
            </button>
          ) : addable && maxQuantity > 0 ? (
            <button className="btn btn-primary btn-lg" onClick={handleAddToCart}>
              Sepete Ekle
            </button>
          ) : addable && maxQuantity === 0 ? (
            <button className="btn btn-primary btn-lg" disabled>
              Stok Limitine Ulaşıldı
            </button>
          ) : product.priceLabel ? (
            <a href="tel:+905535382462" className="btn btn-primary btn-lg">
              Sipariş Ver — +90 553 538 24 62
            </a>
          ) : (
            <button className="btn btn-primary btn-lg" disabled>
              Stokta Yok
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
